import { Box, styled, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { CenteredSpinner } from 'src/components/Spinner'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import NoNews from '../../assets/svg/no-locality-content.svg'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import AllProjects from './containers/AllProjects'
import { useGetCustomerNamesWithProjectPlanningAccess } from 'src/api/customer'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { useUserCtx } from 'src/hooks/context-hooks'
import { permissionValue } from 'src/enums/permissionValue'
import { AuthorizationProvider } from 'src/components/common/Authorization'
import { AuthorizationType } from 'src/enums/auth'
import { Settings } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { AppRoutesNavigation } from 'src/app-routes'
import { axios } from 'src/axios'
import CreateProjectButton from './CreateProjectButton'
import { SearchBar } from 'src/components/common/input/SearchBar'
import ToggleGroupButtons from 'src/components/common/ToggleGroupButtons'
import ProjectFilterComponent from './ProjectFilter'
import { ProjectFilter } from 'src/generated-types/project-filter'
import WindowSharpIcon from '@mui/icons-material/WindowSharp'
import FormatListBulletedSharpIcon from '@mui/icons-material/FormatListBulletedSharp'
import { IProjectStatus } from 'src/generated-types/project-status'

const ProjectMainPage = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery<any>(theme.breakpoints.down('sm'))
  const { data, isLoading, isSuccess } = useGetCustomerNamesWithProjectPlanningAccess()
  const { user } = useUserCtx()
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState('')
  const [cardView, setCardView] = useState(false)
  const { t: translate } = useTranslation(['common'])
  const [filterOptions, setFilterOptions] = useState<ProjectFilter>({
    localityIds: [],
    responsibleUserIds: [],
    ourReferences: [],
    createdDate: { startDate: '', endDate: '' },
    status: [],
  })
  const [menuItems, setMenuItems] = useState<ProjectFilter>({
    localityIds: [],
    responsibleUserIds: [],
    ourReferences: [],
    createdDate: { startDate: '', endDate: '' },
    status: Object.values(IProjectStatus)
      .filter(value => typeof value === 'number')
      // Temporarily hidden from the status filter: OnHold, Cancelled
      .filter(value => value !== IProjectStatus.OnHold && value !== IProjectStatus.Cancelled)
      .map(value => ({ id: value as number, name: IProjectStatus[value as number] })),
  })

  useEffect(() => {
    if (user) {
      axios.defaults.headers['X-Permissions'] = user.encryptedPermission
    }
  }, [user])

  const projectViewToggleButtonsComponent = (
    <ToggleGroupButtons
      actions={[
        {
          title: translate('card', { ns: 'common' }),
          value: true,
          Icon: () => (
            <WindowSharpIcon
              sx={{
                color: cardView
                  ? getThemeColor(theme, MUIThemeColors.white)
                  : getThemeColor(theme, MUIThemeColors.primaryMain),
              }}
            />
          ),
        },
        {
          title: translate('list', { ns: 'common' }),
          value: false,
          Icon: () => (
            <FormatListBulletedSharpIcon
              sx={{
                color: cardView
                  ? getThemeColor(theme, MUIThemeColors.primaryMain)
                  : getThemeColor(theme, MUIThemeColors.white),
              }}
            />
          ),
        },
      ]}
      defaultValue={cardView}
      value={cardView}
      setViewMode={val => {
        setCardView(val)
      }}
      key={'project-view-toggle'}
      height={isMobile ? '40px' : '44px'}
    />
  )

  const projectStatusToggleButtonsComponent = (
    <ToggleGroupButtons
      actions={[
        {
          title: translate('active', { ns: 'common' }),
          value: IProjectStatus.InProcess,
        },
        {
          title: translate('Published', { ns: 'projects' }),
          value: IProjectStatus.Published,
        },
      ]}
      defaultValue={IProjectStatus.InProcess}
      value={
        filterOptions.status.length > 1
          ? -1
          : filterOptions.status.length === 1
            ? filterOptions.status[0].id
            : IProjectStatus.InProcess
      }
      disabled={filterOptions.status.length > 1}
      setViewMode={val => {
        setFilterOptions(prev => ({
          ...prev,
          status:
            val === IProjectStatus.Published
              ? [
                  {
                    id: IProjectStatus.Published,
                    name: IProjectStatus[IProjectStatus.Published],
                  },
                ]
              : [],
        }))
      }}
      key="project-status-toggle"
      height={isMobile ? '40px' : '44px'}
      fitContent
    />
  )

  // Filter component
  const filterComponent = (
    <ProjectFilterComponent
      key={'project-filter'}
      filterOptions={filterOptions}
      setFilterOptions={setFilterOptions}
      menuItems={menuItems}
      setMenuItems={setMenuItems}
    />
  )

  // Search bar component
  const searchBarComponent = (
    <Box key={'project-search'} sx={{ flex: isMobile ? 1 : 'none', minWidth: isMobile ? 0 : undefined }}>
      <SearchBar
        searchInput={searchInput}
        onChange={setSearchInput}
        placeholder={translate('search', { ns: 'common' })}
        height={isMobile ? '40px' : '44px'}
        width={isMobile ? '100%' : undefined}
      />
    </Box>
  )

  // Create project button component
  const createProjectButtonComponent = <CreateProjectButton key={'add-project-button'} />

  // Settings button component
  const settingsButtonComponent = (
    <AuthorizationProvider
      self_auth={true}
      permission={user?.permission}
      permissions={[permissionValue.SystemProjectAdminPermission, permissionValue.SystemAdminPermission]}
      type={AuthorizationType.Disabled}
      key={'system-project-admin'}
    >
      <Tooltip title={String(translate('system-project-admin', { ns: 'projects' }))}>
        <span>
          <GridActionButton
            variant="outlined"
            width={isMobile ? '36px' : '44px'}
            height={isMobile ? '36px' : '44px'}
            onClick={() => {
              navigate(AppRoutesNavigation.ProjectAdmin.users)
            }}
            sx={{
              minWidth: '10px',
              border: `1px solid ${getThemeColor(theme, MUIThemeColors.inputBorder)}`,
            }}
            buttonIcon={<Settings sx={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />}
            bgColor={getThemeColor(theme, MUIThemeColors.white)}
          />
        </span>
      </Tooltip>
    </AuthorizationProvider>
  )

  const MobileTitleComponent = () => {
    return (
      <Box display={'flex'} justifyContent={'space-between'} width={'100%'} gap={1} paddingBottom={1}>
        <Box>
          <Typography color={theme.palette.primaryMain[theme.palette.mode]} variant="h5" fontWeight={600}>
            {translate('projects', { ns: 'projects' })}
          </Typography>
        </Box>
        {settingsButtonComponent}
      </Box>
    )
  }

  // Define top actions based on mobile or desktop view
  const topActions = isMobile
    ? // Mobile layout: Toggle buttons | Filter | Search bar
      [projectViewToggleButtonsComponent, filterComponent, searchBarComponent]
    : // Desktop layout: Search bar | Toggle buttons | Filter | Create Project | Settings
      [
        searchBarComponent,
        projectStatusToggleButtonsComponent,
        projectViewToggleButtonsComponent,
        filterComponent,
        createProjectButtonComponent,
        settingsButtonComponent,
      ]

  return (
    <>
      <Box
        className="relative z-10 h-full overflow-hidden"
        sx={{ width: '100%', backgroundColor: getThemeColor(theme, MUIThemeColors.backgroundMain) }}
      >
        {isLoading && <CenteredSpinner />}
        {isSuccess && (
          <Box className="pt-1">
            <ProjectsContainer>
              <CommonPageLayout
                titleSection={
                  isMobile
                    ? { titleComponent: <MobileTitleComponent /> }
                    : { title: translate('projects', { ns: 'projects' }) }
                }
                topAction={topActions}
                topActionContainerProps={{
                  alignItems: 'center',
                  ...(isMobile && {
                    justifyContent: 'flex-start',
                    width: '100%',
                    gap: 1,
                    flexWrap: 'nowrap',
                  }),
                }}
              >
                {data && data.length === 0 ? (
                  <Box
                    py={8}
                    className="text-center"
                    height={'100%'}
                    sx={{ backgroundColor: getThemeColor(theme, MUIThemeColors.white) }}
                  >
                    <EmptyStatusCard
                      imageWidth="132px"
                      image={NoNews}
                      header={translate('customer-empty-status-title', { ns: 'projects' })}
                      content={translate('customer-empty-status-content', { ns: 'projects' })}
                      headerFontSize="18px"
                      contentFontSize="14px"
                      contentTopMargin="10px"
                      imageContainerClasses="pb-4"
                      height="100%"
                      contentClasses="mt-1"
                    />
                  </Box>
                ) : (
                  <AllProjects
                    customers={data}
                    setFilterOptions={setFilterOptions}
                    filterOptions={filterOptions}
                    setMenuItems={setMenuItems}
                    cardView={cardView}
                    searchInput={searchInput}
                  />
                )}
              </CommonPageLayout>
            </ProjectsContainer>
          </Box>
        )}
      </Box>
      {/* Render FAB separately for mobile - it's fixed positioned */}
      {isMobile && <CreateProjectButton />}
    </>
  )
}

export default ProjectMainPage

const ProjectsContainer = styled(Box)`
  padding: 0 4px 4px 0;
`
